import { Component, EventEmitter, Input, OnChanges, Output, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import {
    ITCIMarketplaceListing,
    ITCIUserInfo,
    ITCIApplication,
    TCI_MARKETPLACE_ACTIONS,
    TCI_MARKETPLACE_LISTING_VIEW_MODE,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    SERVICE_URLS
} from "@tci/common";
import { NgToolsService } from "../../../ng-shared/ng-util/ng-tools.service";
import { CheckOverActionItem } from "../boolean-table/listing-bt-action";
import { CheckOverFooterItem } from "../boolean-table/listing-bt-footer";
import { IMkpV2ListContext, IMkpV2ListEvalRes, MKP_POPUP, MKP_FOOTER_ITEMS } from "../boolean-table/listing-bt-evaluations";
import { NgMarketplaceV2Service } from "../../ng-marketplace-v2.service";
import { NgEnvVarsService } from "../../../ng-shared/ng-services/ng-env-vars.service";
import { NgToastrService } from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { getAppDetailsRoute } from "../../../ng-shared/ng-util/tools";
import { isEmpty } from "lodash";
import { Observable, Subscription } from "rxjs";

@Component({
    selector: "ng-marketplace-v2-get",
    templateUrl: "./ng-marketplace-v2-get.component.html",
    styleUrls: ["./ng-marketplace-v2-get.component.less"]
})
export class NgMarketplaceV2GetComponent implements OnChanges, OnDestroy {
    @Input() asset: ITCIMarketplaceListing;
    @Input() enabled = false;
    @Input() userInfo: ITCIUserInfo;
    @Input() mode: TCI_MARKETPLACE_LISTING_VIEW_MODE = TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD;

    @Output() closePopup = new EventEmitter();
    @Output() isBusy: EventEmitter<boolean> = new EventEmitter();
    @Output() haveInstalled = new EventEmitter();

    assetType: {
        scribeConnector?: IMkpV2ListEvalRes,
        connector?: IMkpV2ListEvalRes,
        application?: IMkpV2ListEvalRes
    } = {};

    private componentIsAlive = true;
    isGlobalyEnabled: boolean;
    showPopup: boolean;
    showAfterInstall = false;
    agreed: boolean;
    creatorAppName = ""; // If type is "app" we need to requests the creator name
    eulaUrl = "";
    showEULA = false;

    private _busy: boolean;
    get busy() {
        return this._busy;
    }
    set busy(val: boolean) {
        this._busy = val;
        this.isBusy.emit(val);
    }

    private subscriptions: Subscription[] = [];

    constructor(
        private mktSvc: NgMarketplaceV2Service,
        private toastrSvc: NgToastrService,
        private envSvc: NgEnvVarsService,
        private tools: NgToolsService,
        private router: Router
    ) {
        this.subscriptions.push(
            mktSvc.getNotificationSubject()
                .filter(change => this.asset && change.listing.id === this.asset.id)
                .subscribe(() => this.getEULA())
        );
    }

    ngOnChanges() {
        this.init();
    }

    ngOnDestroy() {
        this.componentIsAlive = false;
        this.subscriptions
            .filter(s => s && !s.closed)
            .forEach(s => s.unsubscribe());
    }

    private init() {
        if (!this.componentIsAlive) {
            console.warn("NgMarketplaceV2GetComponent is not available");
            return;
        }

        this.showPopup = false;
        this.agreed = false;
        this.busy = false;
        this.evaluateAssetType();
        this.getEULA();

        this.isGlobalyEnabled = this.enabled &&
            [this.assetType.scribeConnector, this.assetType.connector, this.assetType.application].some(a => a.enabled);
    }

    private getCheckItemContext(item: MKP_POPUP | MKP_FOOTER_ITEMS): IMkpV2ListContext {
        return {
            item,
            asset: this.asset,
            mode: this.mode,
            userInfo: this.userInfo
        };
    }

    private evaluateAssetType() {
        if (this.enabled && this.userInfo) {
            this.assetType.scribeConnector = CheckOverActionItem(this.getCheckItemContext(MKP_POPUP.GET_ENABLE_SCRIBE));
            this.assetType.connector = CheckOverActionItem(this.getCheckItemContext(MKP_POPUP.GET_ENABLE_CONNECTOR));
            this.assetType.application = CheckOverActionItem(this.getCheckItemContext(MKP_POPUP.GET_ENABLE_APP));
        } else {
            this.assetType.scribeConnector = { visible: false };
            this.assetType.connector = { visible: false };
            this.assetType.application = { visible: false };
        }
    }

    private getEULA() {
        this.showEULA = CheckOverFooterItem(this.getCheckItemContext(MKP_FOOTER_ITEMS.EULA_LINK)).visible;

        if (this.showEULA) {
            this.eulaUrl = this.asset.eulaUrl;
            if (isEmpty(this.eulaUrl)) {
                this.eulaUrl = `${SERVICE_URLS.MARKETPLACE_EULA}`;
            }
        } else { // If no EULA then activate button by default
            this.agreed = true;
        }
    }

    togglePopup() {
        if (!this.busy) {
            if (!this.showPopup) {
                this.showPopup = true;
            } else {
                this.callClosePopup();
            }
        }
    }

    callClosePopup() {
        if (!this.busy) {
            this.showPopup = false;
            this.showAfterInstall = false;
            this.closePopup.emit();
        }
    }

    // Install or request Get
    install() {
        if (this.agreed) {
            if (this.assetType.application.isDownloadable) {
                this.mktSvc.processAction({ actionType: TCI_MARKETPLACE_ACTIONS.GetAccess, listing: this.asset })
                    .do(() => this.init())
                    .subscribe(r => {
                        if (r.success) {
                            this.mktSvc.downloadAssets(this.asset.id)
                            .catch(err => {
                                return Observable.throw(err);
                            })
                            .subscribe((res: { [key: string]: Blob }) => {
                                const keys: string[] = Object.keys(res);
                                if (keys.length > 0) {
                                    const fileName = keys[0];
                                    this.launchDownload(res[fileName], fileName);
                                    this.toastrSvc.success("Successfully downloaded Application Assets");
                                }
                            }, error => {
                                this.toastrSvc.error("Failed to Download Assets", error.message);
                            });
                        } else {
                          this.toastrSvc.error("Failed to Download Assets", r.msg || "");
                        }
                    });
            } else if (this.assetType.scribeConnector.visible) { // Is connector or app: Install/Get
                this.executeRequest(TCI_MARKETPLACE_ACTIONS.Install)
                    .subscribe();
            } else {
                this.executeRequest(TCI_MARKETPLACE_ACTIONS.GetAccess)
                    .filter(r => !!r && this.assetType.application.visible)
                    .subscribe(() => {
                        // Navigate to application's details
                        const path = getAppDetailsRoute({ type: this.asset.tenantType, id: this.asset.createdAssetId } as ITCIApplication);
                        this.router.navigate(["applications", ...path.route], { queryParams: path.queryParams });
                    });
            }
        }
    }


    private launchDownload(fileContent: Blob, fileName: string): void {
        this.tools.download(fileName, fileContent);
    }

    private executeRequest(actionType: TCI_MARKETPLACE_ACTIONS): Observable<boolean> {
        this.busy = true;
        this.mktSvc.creatingAppFromMiniListSubject(true);
        return this.mktSvc.processAction({ actionType, listing: this.asset })
            .do(() => this.init())
            .map(res => {
                this.mktSvc.creatingAppFromMiniListSubject(false);
                if (res.success) {
                    this.asset = Object.assign(this.asset, res.data[0]);
                    this.mktSvc.notifyChange({ listing: res.data[0], type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Update });
                    this.haveInstalled.emit();
                    if (this.assetType.connector.visible) { // IPAS-17109 - Flogo connector install should display GET with popup
                        this.showAfterInstall = true;
                    } else if (this.assetType.application.visible) {
                        this.toastrSvc.success("A new application has been created");
                    }
                    return true;
                } else {
                    this.toastrSvc.error(res.error.message);
                }

                return res.success;
            });
    }
}
